import * as React from 'react';

// BeefedUp brand artwork as inline SVG: the marbled crimson cube + silver arc mark, product illustrations, icons.

let uid = 0;
const useId = (p: string): string => React.useMemo((): string => `${p}${++uid}`, [p]);

/** The BeefedUp mark: isometric crimson cube with marbled veins and the silver arc. */
export const LogoMark: React.FunctionComponent<{ size?: number }> = ({ size = 32 }) => {
  const id = useId('lm');
  return (
    <svg width={size} height={size} viewBox="0 0 48 48" aria-hidden="true">
      <defs>
        <linearGradient id={`${id}t`} x1="0" y1="0" x2="0" y2="1"><stop offset="0" stopColor="#f0a3a6" /><stop offset="1" stopColor="#c8343a" /></linearGradient>
        <linearGradient id={`${id}l`} x1="0" y1="0" x2="1" y2="1"><stop offset="0" stopColor="#a8191f" /><stop offset="1" stopColor="#5c0c10" /></linearGradient>
        <linearGradient id={`${id}r`} x1="1" y1="0" x2="0" y2="1"><stop offset="0" stopColor="#c42a2f" /><stop offset="1" stopColor="#7a1015" /></linearGradient>
        <linearGradient id={`${id}s`} x1="0" y1="0" x2="1" y2="1"><stop offset="0" stopColor="#ffffff" /><stop offset=".5" stopColor="#b8bcc3" /><stop offset="1" stopColor="#eef0f2" /></linearGradient>
      </defs>
      <path d="M24 9 L39 17.5 L24 26 L9 17.5 Z" fill={`url(#${id}t)`} />
      <path d="M9 17.5 L24 26 L24 43 L9 34.5 Z" fill={`url(#${id}l)`} />
      <path d="M39 17.5 L24 26 L24 43 L39 34.5 Z" fill={`url(#${id}r)`} />
      <g fill="none" stroke="#fff" strokeWidth=".6" strokeLinecap="round" opacity=".55">
        <path d="M13 20 L17 24 L16 29 L19 33" /><path d="M11 27 L15 28 L18 31" /><path d="M28 30 L31 27 L35 28 L36 24" />
        <path d="M27 38 L30 34 L34 33" /><path d="M17 15 L22 17 L26 15 L31 17" />
      </g>
      <path d="M9 17.5 L24 26 L39 17.5 M24 26 L24 43" fill="none" stroke="#fff" strokeOpacity=".35" strokeWidth=".7" />
      <path d="M33.5 3.5 A 19 19 0 0 1 44.5 23" fill="none" stroke={`url(#${id}s)`} strokeWidth="2.4" strokeLinecap="round" />
    </svg>
  );
};

export const Wordmark: React.FunctionComponent<{ suffix?: string }> = ({ suffix = 'Apps' }) => (
  <span className="bu-brand-name"><b>BeefedUp</b> <span>{suffix}</span></span>
);

export const BrandLockup: React.FunctionComponent<{ suffix?: string; size?: number }> = ({ suffix, size = 30 }) => (
  <span className="bu-brand"><LogoMark size={size} /><Wordmark suffix={suffix} /></span>
);

// ---------------------------------------------------------------------------
// Hero illustration: the product - a batch dashboard with lots and stock, floating over the brand cube.
// ---------------------------------------------------------------------------

export const HeroIllustration: React.FunctionComponent = () => {
  const id = useId('hero');
  return (
    <svg viewBox="0 0 560 470" role="img" aria-label="BeefedUp Apps dashboard showing a production batch, inventory lots and stock levels">
      <defs>
        <radialGradient id={`${id}glow`} cx="50%" cy="50%" r="50%"><stop offset="0" stopColor="#c42a2f" stopOpacity=".55" /><stop offset="1" stopColor="#c42a2f" stopOpacity="0" /></radialGradient>
        <linearGradient id={`${id}win`} x1="0" y1="0" x2="0" y2="1"><stop offset="0" stopColor="#202328" /><stop offset="1" stopColor="#16181c" /></linearGradient>
        <linearGradient id={`${id}bar`} x1="0" y1="0" x2="0" y2="1"><stop offset="0" stopColor="#e2474c" /><stop offset="1" stopColor="#8f141a" /></linearGradient>
        <linearGradient id={`${id}t`} x1="0" y1="0" x2="0" y2="1"><stop offset="0" stopColor="#f0a3a6" /><stop offset="1" stopColor="#c8343a" /></linearGradient>
        <linearGradient id={`${id}l`} x1="0" y1="0" x2="1" y2="1"><stop offset="0" stopColor="#a8191f" /><stop offset="1" stopColor="#4a0a0d" /></linearGradient>
        <linearGradient id={`${id}r`} x1="1" y1="0" x2="0" y2="1"><stop offset="0" stopColor="#c42a2f" /><stop offset="1" stopColor="#6e0f14" /></linearGradient>
        <linearGradient id={`${id}s`} x1="0" y1="0" x2="1" y2="1"><stop offset="0" stopColor="#fff" /><stop offset=".55" stopColor="#9aa0a8" /><stop offset="1" stopColor="#e4e6e9" /></linearGradient>
      </defs>

      <ellipse cx="400" cy="150" rx="190" ry="150" fill={`url(#${id}glow)`} />
      {/* brand cube */}
      <g transform="translate(330 30)">
        <path d="M80 0 L160 46 L80 92 L0 46 Z" fill={`url(#${id}t)`} />
        <path d="M0 46 L80 92 L80 184 L0 138 Z" fill={`url(#${id}l)`} />
        <path d="M160 46 L80 92 L80 184 L160 138 Z" fill={`url(#${id}r)`} />
        <g fill="none" stroke="#fff" strokeWidth="1.1" strokeLinecap="round" opacity=".42">
          <path d="M14 62 L34 80 L30 104 L46 122 L44 150" /><path d="M8 100 L26 106 L40 124" /><path d="M96 120 L112 104 L134 108 L140 86" />
          <path d="M92 160 L108 140 L132 134 L150 112" /><path d="M36 30 L62 40 L84 30 L112 40 L130 34" /><path d="M58 52 L80 58 L104 50" />
        </g>
        <path d="M0 46 L80 92 L160 46 M80 92 L80 184" fill="none" stroke="#fff" strokeOpacity=".3" strokeWidth="1.2" />
        <path d="M118 -18 A 100 100 0 0 1 182 84" fill="none" stroke={`url(#${id}s)`} strokeWidth="6" strokeLinecap="round" />
      </g>

      {/* app window */}
      <g transform="translate(10 120)">
        <rect width="410" height="300" rx="16" fill={`url(#${id}win)`} stroke="#2c3036" />
        <circle cx="22" cy="22" r="5" fill="#454a52" /><circle cx="38" cy="22" r="5" fill="#454a52" /><circle cx="54" cy="22" r="5" fill="#454a52" />
        <rect x="80" y="15" width="150" height="14" rx="7" fill="#2c3036" />
        <line x1="0" y1="44" x2="410" y2="44" stroke="#2c3036" />

        <text x="22" y="74" fill="#8a9099" fontFamily="Manrope, sans-serif" fontSize="10" fontWeight="700" letterSpacing="1.2">BATCH 128 · SPARKLING WATER</text>
        <text x="22" y="98" fill="#fff" fontFamily="Sora, sans-serif" fontSize="20" fontWeight="600">24,000 units</text>
        <g transform="translate(22 114)">
          {[46, 70, 58, 88, 64, 96, 78].map((h: number, i: number) => (
            <rect key={i} x={i * 24} y={96 - h} width="14" height={h} rx="3" fill={i === 5 ? `url(#${id}bar)` : '#2c3036'} />
          ))}
          <line x1="0" y1="97" x2="166" y2="97" stroke="#2c3036" />
        </g>

        <g transform="translate(206 60)">
          <text x="0" y="12" fill="#8a9099" fontFamily="Manrope, sans-serif" fontSize="10" fontWeight="700" letterSpacing="1.2">STOCK BY LOT</text>
          {[
            { n: 'Citric acid', l: 'L-2041', w: 132, c: '#1f7a4d' },
            { n: 'Black cherry', l: 'L-1987', w: 86, c: '#c79a2c' },
            { n: '12oz cans', l: 'C-0553', w: 140, c: '#1f7a4d' },
            { n: 'Sucralose', l: 'L-2102', w: 40, c: '#c42a2f' }
          ].map((r, i: number) => (
            <g key={i} transform={`translate(0 ${24 + i * 40})`}>
              <text x="0" y="10" fill="#e4e6e9" fontFamily="Manrope, sans-serif" fontSize="12" fontWeight="700">{r.n}</text>
              <rect x="100" y="0" width="66" height="16" rx="8" fill="#2c3036" />
              <text x="133" y="11.5" fill="#b3b8bf" fontFamily="JetBrains Mono, monospace" fontSize="9" textAnchor="middle">{r.l}</text>
              <rect x="0" y="22" width="166" height="6" rx="3" fill="#2c3036" />
              <rect x="0" y="22" width={r.w} height="6" rx="3" fill={r.c} />
            </g>
          ))}
        </g>
        <rect x="22" y="236" width="366" height="44" rx="10" fill="#1b1d21" stroke="#2c3036" />
        <circle cx="46" cy="258" r="11" fill="#1f7a4d" /><path d="M41 258 l3.5 3.5 l6.5 -7" stroke="#fff" strokeWidth="2.2" fill="none" strokeLinecap="round" strokeLinejoin="round" />
        <text x="66" y="255" fill="#fff" fontFamily="Manrope, sans-serif" fontSize="12" fontWeight="700">Batch 128 completed</text>
        <text x="66" y="270" fill="#8a9099" fontFamily="Manrope, sans-serif" fontSize="10.5">9 lots drawn · earliest expiry first</text>
        <rect x="300" y="248" width="74" height="20" rx="10" fill="#a8191f" />
        <text x="337" y="261.5" fill="#fff" fontFamily="Manrope, sans-serif" fontSize="10" fontWeight="700" textAnchor="middle">PDF ready</text>
      </g>

      {/* floating lot card */}
      <g transform="translate(398 318)">
        <rect width="156" height="92" rx="14" fill="#fff" />
        <text x="14" y="24" fill="#646a73" fontFamily="Manrope, sans-serif" fontSize="9" fontWeight="700" letterSpacing="1.1">LOT RECEIVED</text>
        <text x="14" y="45" fill="#15171a" fontFamily="Sora, sans-serif" fontSize="14.5" fontWeight="600">Citric acid</text>
        <text x="14" y="62" fill="#454a52" fontFamily="JetBrains Mono, monospace" fontSize="9.5">L-2041 · 25.0 kg</text>
        <rect x="14" y="70" width="90" height="14" rx="7" fill="#fdf3dc" />
        <text x="59" y="80" fill="#8a5a00" fontFamily="Manrope, sans-serif" fontSize="8.5" fontWeight="700" textAnchor="middle">EXP 2027-01-31</text>
      </g>
    </svg>
  );
};

// ---------------------------------------------------------------------------
// App artwork
// ---------------------------------------------------------------------------

export const SeltzerArt: React.FunctionComponent<{ height?: number }> = ({ height = 104 }) => {
  const id = useId('sa');
  return (
    <svg height={height} viewBox="0 0 220 110" aria-hidden="true">
      <defs>
        <linearGradient id={`${id}can`} x1="0" y1="0" x2="1" y2="0"><stop offset="0" stopColor="#6e0f14" /><stop offset=".35" stopColor="#c42a2f" /><stop offset=".6" stopColor="#e2474c" /><stop offset="1" stopColor="#8f141a" /></linearGradient>
        <linearGradient id={`${id}met`} x1="0" y1="0" x2="1" y2="0"><stop offset="0" stopColor="#8a9099" /><stop offset=".5" stopColor="#f6f7f8" /><stop offset="1" stopColor="#8a9099" /></linearGradient>
        <linearGradient id={`${id}tank`} x1="0" y1="0" x2="1" y2="0"><stop offset="0" stopColor="#2c3036" /><stop offset=".5" stopColor="#5a6069" /><stop offset="1" stopColor="#2c3036" /></linearGradient>
      </defs>
      {/* tank */}
      <rect x="22" y="24" width="56" height="70" rx="10" fill={`url(#${id}tank)`} />
      <ellipse cx="50" cy="24" rx="28" ry="7" fill="#646a73" />
      <rect x="30" y="44" width="40" height="40" rx="5" fill="#15171a" opacity=".55" />
      <rect x="30" y="62" width="40" height="22" rx="4" fill="#c42a2f" opacity=".85" />
      <line x1="34" y1="94" x2="34" y2="104" stroke="#454a52" strokeWidth="3" /><line x1="66" y1="94" x2="66" y2="104" stroke="#454a52" strokeWidth="3" />
      <path d="M78 52 H108 V40" fill="none" stroke="#454a52" strokeWidth="4" strokeLinecap="round" />
      {/* can */}
      <rect x="118" y="22" width="48" height="80" rx="9" fill={`url(#${id}can)`} />
      <rect x="120" y="16" width="44" height="9" rx="4" fill={`url(#${id}met)`} />
      <rect x="120" y="98" width="44" height="7" rx="3" fill={`url(#${id}met)`} />
      <rect x="126" y="48" width="32" height="26" rx="4" fill="#fff" opacity=".92" />
      <path d="M142 52 L150 56.5 L142 61 L134 56.5 Z" fill="#c42a2f" /><path d="M134 56.5 L142 61 L142 70 L134 65.5 Z" fill="#8f141a" /><path d="M150 56.5 L142 61 L142 70 L150 65.5 Z" fill="#a8191f" />
      {/* bubbles */}
      {[[176, 30, 4], [186, 46, 2.6], [180, 62, 3.4], [194, 20, 2.2], [198, 70, 2], [172, 80, 2.4]].map((b: number[], i: number) => (
        <circle key={i} cx={b[0]} cy={b[1]} r={b[2]} fill="none" stroke="#b3b8bf" strokeWidth="1.4" opacity=".8" />
      ))}
    </svg>
  );
};

/** Isometric cube cluster for the launcher banner. */
export const CubeCluster: React.FunctionComponent<{ className?: string }> = ({ className }) => {
  const cube = (x: number, y: number, s: number, o: number, key: number): React.ReactElement => (
    <g key={key} transform={`translate(${x} ${y}) scale(${s})`} opacity={o}>
      <path d="M40 0 L80 23 L40 46 L0 23 Z" fill="#e2474c" />
      <path d="M0 23 L40 46 L40 92 L0 69 Z" fill="#8f141a" />
      <path d="M80 23 L40 46 L40 92 L80 69 Z" fill="#a8191f" />
      <path d="M0 23 L40 46 L80 23 M40 46 L40 92" fill="none" stroke="#fff" strokeOpacity=".25" />
    </g>
  );
  return (
    <svg className={className} viewBox="0 0 360 240" aria-hidden="true">
      {cube(150, 40, 1.3, 1, 1)}{cube(70, 110, .8, .55, 2)}{cube(255, 120, .7, .45, 3)}{cube(230, 10, .45, .35, 4)}{cube(40, 20, .35, .25, 5)}
      <path d="M265 30 A 90 90 0 0 1 330 140" fill="none" stroke="#c9cdd3" strokeWidth="4" strokeLinecap="round" opacity=".8" />
    </svg>
  );
};

export const EmptyArt: React.FunctionComponent = () => (
  <svg width="120" height="90" viewBox="0 0 120 90" aria-hidden="true">
    <path d="M60 10 L100 30 L60 50 L20 30 Z" fill="#eef0f2" stroke="#d7dade" />
    <path d="M20 30 L60 50 L60 82 L20 62 Z" fill="#e4e6e9" stroke="#d7dade" />
    <path d="M100 30 L60 50 L60 82 L100 62 Z" fill="#f6f7f8" stroke="#d7dade" />
    <path d="M40 20 L80 40" stroke="#c42a2f" strokeWidth="2" strokeDasharray="4 4" />
  </svg>
);

// ---------------------------------------------------------------------------
// Identity provider marks (official colours) for the sign-in buttons
// ---------------------------------------------------------------------------

export const MicrosoftMark: React.FunctionComponent<{ size?: number }> = ({ size = 20 }) => (
  <svg width={size} height={size} viewBox="0 0 21 21" aria-hidden="true">
    <rect x="1" y="1" width="9" height="9" fill="#f25022" /><rect x="11" y="1" width="9" height="9" fill="#7fba00" />
    <rect x="1" y="11" width="9" height="9" fill="#00a4ef" /><rect x="11" y="11" width="9" height="9" fill="#ffb900" />
  </svg>
);

export const GoogleMark: React.FunctionComponent<{ size?: number }> = ({ size = 20 }) => (
  <svg width={size} height={size} viewBox="0 0 48 48" aria-hidden="true">
    <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z" />
    <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z" />
    <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z" />
    <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z" />
  </svg>
);

// ---------------------------------------------------------------------------
// Line icons (24px grid, 1.8 stroke)
// ---------------------------------------------------------------------------

type IconName = 'flask' | 'layers' | 'shield' | 'users' | 'building' | 'lock' | 'check' | 'arrow' | 'grid' | 'file' | 'logout' | 'plus' | 'link' | 'copy' | 'chevron' | 'spark' | 'database' | 'key' | 'mail';
const PATHS: { [k in IconName]: string } = {
  flask: 'M9 3h6M10 3v6.5L4.6 18.2A2 2 0 0 0 6.3 21h11.4a2 2 0 0 0 1.7-2.8L14 9.5V3M7.5 15h9',
  layers: 'M12 3 3 8l9 5 9-5-9-5zM3 13l9 5 9-5M3 17.5l9 5 9-5',
  shield: 'M12 3l8 3v6c0 4.5-3.4 8.3-8 9-4.6-.7-8-4.5-8-9V6l8-3zM9 12l2 2 4-4',
  users: 'M16 20v-1.5a3.5 3.5 0 0 0-3.5-3.5h-5A3.5 3.5 0 0 0 4 18.5V20M10 11a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7zM20 20v-1.5a3.5 3.5 0 0 0-2.5-3.35M15.5 4.15a3.5 3.5 0 0 1 0 6.7',
  building: 'M4 21V5a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v16M16 9h2a2 2 0 0 1 2 2v10M8 7h4M8 11h4M8 15h4M2 21h20',
  lock: 'M6 11h12v10H6zM8.5 11V7.5a3.5 3.5 0 0 1 7 0V11M12 15v2',
  check: 'M5 12.5l4.5 4.5L19 7.5',
  arrow: 'M5 12h14M13 6l6 6-6 6',
  grid: 'M4 4h7v7H4zM13 4h7v7h-7zM4 13h7v7H4zM13 13h7v7h-7z',
  file: 'M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8l-5-5zM14 3v5h5M9 13h6M9 17h6',
  logout: 'M15 4h3a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2h-3M10 16l-4-4 4-4M6 12h10',
  plus: 'M12 5v14M5 12h14',
  link: 'M10 14a4 4 0 0 0 5.66 0l3-3a4 4 0 0 0-5.66-5.66l-1 1M14 10a4 4 0 0 0-5.66 0l-3 3a4 4 0 0 0 5.66 5.66l1-1',
  copy: 'M9 9h10v10H9zM5 15V5h10',
  chevron: 'M9 6l6 6-6 6',
  spark: 'M12 3v4M12 17v4M3 12h4M17 12h4M6 6l2.5 2.5M15.5 15.5 18 18M6 18l2.5-2.5M15.5 8.5 18 6',
  database: 'M12 3c4.4 0 8 1.3 8 3s-3.6 3-8 3-8-1.3-8-3 3.6-3 8-3zM4 6v12c0 1.7 3.6 3 8 3s8-1.3 8-3V6M4 12c0 1.7 3.6 3 8 3s8-1.3 8-3',
  key: 'M15 7a4 4 0 1 1-3.87 5H9v2H7v2H4v-3l5.13-5.13A4 4 0 0 1 15 7zM16 9h.01',
  mail: 'M4 6h16v12H4zM4 7l8 6 8-6'
};

export const Icon: React.FunctionComponent<{ name: IconName; size?: number; className?: string }> = ({ name, size = 20, className }) => (
  <svg className={className} width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d={PATHS[name]} />
  </svg>
);

export const Spinner: React.FunctionComponent = () => (
  <svg className="bu-spin" width="40" height="40" viewBox="0 0 48 48" aria-label="Loading"><LogoMarkPaths /></svg>
);
const LogoMarkPaths: React.FunctionComponent = () => (
  <g>
    <path d="M24 9 L39 17.5 L24 26 L9 17.5 Z" fill="#e2474c" /><path d="M9 17.5 L24 26 L24 43 L9 34.5 Z" fill="#8f141a" /><path d="M39 17.5 L24 26 L24 43 L39 34.5 Z" fill="#a8191f" />
  </g>
);
